import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { CasheaAnnouncementModalComponent } from '@common/components/cashea-announcement-modal/cashea-announcement-modal.component';
import { CASHEA_ANNOUNCEMENT_FLAG, CASHEA_ANNOUNCEMENT_SEEN_KEY, CasheaAnnouncementVariant } from '@common/constants/cashea-announcement';
import { CASHEA_LINK_PAUSED_FLAG } from '@common/constants/cashea-link-flag';
import { readSupportSession } from '@modules/auth/models';
import { UserService } from '@modules/auth/services';
import { LineMilestoneWatcherService } from '@modules/creditos/services/line-milestone-watcher.service';
import { casheaMethodsOf } from '@modules/pos/services/cashea.service';
import { ModalDismissReasons, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, EMPTY, interval, Subscription, timer } from 'rxjs';
import { distinctUntilChanged, filter, map, scan, startWith, switchMap, take, takeUntil } from 'rxjs/operators';

import { AnalyticsService } from './analytics.service';
import { InstructionsModalService } from './instructions-modal.service';

export interface CasheaAnnouncementAudience {
    userId: string;
    variant: CasheaAnnouncementVariant;
    canRequest: boolean;
}

/** Ruta de la pestaña de Cashea, donde se abre el formulario de solicitud. */
export const CASHEA_TAB_ROUTE = '/accounts/pending-accounts/cashea';

/** Margen tras entrar para que los modales del login (noticias, pagos, Fina Presta, fiscal) se abran primero. */
const SETTLE_MS = 4000;
const CHECK_EVERY_MS = 500;
/** Chequeos seguidos sin nada abierto antes de mostrarlo: así no aparece en el hueco entre dos modales. */
const QUIET_CHECKS = 3;
/** Si en este rato nunca hubo un hueco, se intenta en el próximo inicio de sesión. */
const GIVE_UP_MS = 10 * 60 * 1000;

/**
 * Anuncio de Cashea al entrar, una vez por usuario (en cualquier dispositivo).
 *
 * Va último: espera a que no haya ningún modal, video tutorial, tour de onboarding ni modal de Fina
 * Presta abierto, porque esos se abren solos al entrar y no se coordinan con una cola central. Con el
 * pago vencido no aparece (ese modal no se puede cerrar). Solo se marca como visto si el usuario lo
 * cierra o toca el botón; si otro modal lo saca (por ejemplo, el de pago), vuelve a intentarse el
 * próximo inicio de sesión.
 */
@Injectable({ providedIn: 'root' })
export class CasheaAnnouncementService implements OnDestroy {
    private subscription: Subscription | null = null;
    /** Usuarios a los que ya se les evaluó en esta sesión del navegador. */
    private readonly handled = new Set<string>();

    constructor(
        private userService: UserService,
        private analyticsService: AnalyticsService,
        private instructionsModalService: InstructionsModalService,
        private lineMilestone: LineMilestoneWatcherService,
        private modalService: NgbModal,
        private router: Router,
    ) {}

    /** Lo llama el shell una sola vez; se evalúa cada vez que entra un usuario. */
    start(): void {
        if (this.subscription) return;
        this.subscription = combineLatest([this.userService.user$, this.userService.tenant$, this.analyticsService.flagsUpdated$.pipe(startWith(null))])
            .pipe(
                map(([user, tenant]) => this.audienceFor(user, tenant)),
                distinctUntilChanged((a, b) => a?.userId === b?.userId && a?.variant === b?.variant),
                switchMap(audience => (audience ? this.waitForQuiet().pipe(map(() => audience)) : EMPTY)),
            )
            .subscribe(audience => this.open(audience));
    }

    /** A quién mostrárselo, o null si no corresponde (flag, ya visto, ya tiene QR y Link, soporte, pago vencido). */
    audienceFor(user: any, tenant: any): CasheaAnnouncementAudience | null {
        const userId = user?._id ? String(user._id) : null;
        if (!userId || !tenant || this.handled.has(userId)) return null;
        if (!this.analyticsService.getExperimentVariant(CASHEA_ANNOUNCEMENT_FLAG)) return null;
        if (user.instructionsNotToShow?.includes(CASHEA_ANNOUNCEMENT_SEEN_KEY)) return null;
        // Un agente de soporte entrando a la cuenta no debería gastarle el anuncio al comercio.
        if (readSupportSession()) return null;
        if (tenant.statusTenant === 'INACTIVE') return null;

        const methods = casheaMethodsOf(tenant);
        const enabled = tenant.cashea?.enabled === true;
        if (enabled && methods.qr && methods.link) return null;
        const variant: CasheaAnnouncementVariant = enabled && methods.qr ? 'link' : 'connect';
        if (variant === 'link' && this.analyticsService.getExperimentVariant(CASHEA_LINK_PAUSED_FLAG)) return null;
        const canRequest = Boolean(user.permissions?.BANK_ACCOUNTS?.includes('view-pending-accounts'));
        return { userId, variant, canRequest };
    }

    /** Si hay algo encima de la pantalla. Casi todos los modales del sistema llevan `aria-modal`; el fiscal no. */
    isBusy(): boolean {
        return this.modalService.hasOpenModals() || this.instructionsModalService.isModalOpen || this.lineMilestone.isOpen || this.lineMilestone.isBlocked || this.overlayInDom();
    }

    /** Modales propios de cada pantalla (Fina Presta, tours, celebración, fiscal) que no pasan por NgbModal. */
    overlayInDom(): boolean {
        return !!document.querySelector('[aria-modal="true"], .frm-overlay');
    }

    private waitForQuiet() {
        return timer(SETTLE_MS).pipe(
            switchMap(() => interval(CHECK_EVERY_MS)),
            map(() => this.isBusy()),
            scan((quiet, busy) => (busy ? 0 : quiet + 1), 0),
            filter(quiet => quiet >= QUIET_CHECKS),
            take(1),
            takeUntil(timer(GIVE_UP_MS)),
        );
    }

    private open(audience: CasheaAnnouncementAudience): void {
        // Re-chequeo final: el flag o el usuario pudieron cambiar mientras se esperaba el hueco.
        const current = this.audienceFor(this.userService.user$.getValue(), this.userService.tenant$.getValue());
        if (!current || current.userId !== audience.userId) return;
        this.handled.add(audience.userId);

        const ref = this.modalService.open(CasheaAnnouncementModalComponent, {
            centered: true,
            size: 'lg',
            windowClass: 'cashea-announcement-window',
            ariaLabelledBy: 'can-title',
        });
        ref.componentInstance.variant = audience.variant;
        ref.componentInstance.canRequest = audience.canRequest;

        const props = { variant: audience.variant, can_request: audience.canRequest };
        this.analyticsService.analyticsCapture('cashea_announcement_viewed', props);

        ref.result.then(
            () => {
                this.markSeen();
                this.analyticsService.analyticsCapture('cashea_announcement_cta_clicked', props);
                this.router.navigate([CASHEA_TAB_ROUTE], { queryParams: { solicitar: audience.variant } });
            },
            reason => {
                // `dismissAll()` de otro modal (por ejemplo, el de pago vencido) cierra sin motivo: no cuenta como visto.
                if (reason === undefined || reason === null) return;
                this.markSeen();
                const source = reason === ModalDismissReasons.BACKDROP_CLICK ? 'backdrop' : reason === ModalDismissReasons.ESC ? 'esc' : String(reason);
                this.analyticsService.analyticsCapture('cashea_announcement_dismissed', { ...props, dismiss_source: source });
            },
        );
    }

    private markSeen(): void {
        this.instructionsModalService.markDoNotShowAgainSilently(CASHEA_ANNOUNCEMENT_SEEN_KEY);
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }
}
